(function () {
  const TIME_ZONE = 'Europe/Paris';
  const DEFAULT_RESPONSE_MINUTES = 30;
  const WEEKDAY_BY_NAME = {
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
    Sun: 7,
  };
  const FRENCH_WEEKDAYS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];

  function number(value, fallback) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function parseDays(value) {
    const days = Array.isArray(value) ? value : String(value || '').split(',');
    const parsed = days.map((day) => number(day, NaN)).filter((day) => day >= 1 && day <= 7);
    return parsed.length ? parsed : [1, 2, 3, 4, 5];
  }

  function partsFor(date, timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      weekday: 'short',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hourCycle: 'h23',
    }).formatToParts(date);
    const data = Object.fromEntries(parts.map((part) => [part.type, part.value]));

    return {
      year: number(data.year, 0),
      month: number(data.month, 0),
      day: number(data.day, 0),
      hour: number(data.hour, 0),
      minute: number(data.minute, 0),
      weekday: WEEKDAY_BY_NAME[data.weekday] || 1,
    };
  }

  function partsForLocalOffset(parts, offset, timeZone) {
    return partsFor(new Date(Date.UTC(parts.year, parts.month - 1, parts.day + offset, 12)), timeZone);
  }

  function isWithinOfficeHours(parts, days, startHour, endHour) {
    if (!days.includes(parts.weekday)) return false;

    const currentMinutes = parts.hour * 60 + parts.minute;
    const startMinutes = startHour * 60;
    const endMinutes = endHour * 60;

    if (startMinutes === endMinutes) return true;
    if (startMinutes < endMinutes) return currentMinutes >= startMinutes && currentMinutes < endMinutes;
    return currentMinutes >= startMinutes || currentMinutes < endMinutes;
  }

  function formatTime(totalMinutes, locale) {
    const clamped = Math.max(0, Math.min(totalMinutes, 23 * 60 + 59));
    const hour = Math.floor(clamped / 60);
    const minute = String(clamped % 60).padStart(2, '0');
    return String(locale || '').toLowerCase().startsWith('fr') ? `${hour}h${minute}` : `${String(hour).padStart(2, '0')}:${minute}`;
  }

  function dayLabel(offset, weekday, locale, timeZone, labels) {
    const relativeLabels = labels || {};

    if (offset === 0 && relativeLabels.today) return relativeLabels.today;
    if (offset === 1 && relativeLabels.tomorrow) return relativeLabels.tomorrow;

    if (String(locale || '').toLowerCase().startsWith('fr')) {
      if (offset === 0) return 'aujourd’hui';
      if (offset === 1) return 'demain';
      return FRENCH_WEEKDAYS[weekday % 7] || 'prochain jour ouvré';
    }

    if (offset === 0) return 'today';
    if (offset === 1) return 'tomorrow';

    const date = new Date(Date.UTC(2026, 0, 4 + weekday, 12));
    return new Intl.DateTimeFormat(locale || 'en', { weekday: 'long', timeZone }).format(date).toLowerCase();
  }

  function nextOpening(now, options) {
    const config = options || {};
    const timeZone = config.timeZone || TIME_ZONE;
    const locale = config.locale || 'fr';
    const days = parseDays(config.days);
    const startHour = number(config.startHour, 10);
    const endHour = number(config.endHour, 18);
    const responseMinutes = number(config.responseMinutes, DEFAULT_RESPONSE_MINUTES);
    const nowParts = partsFor(now instanceof Date ? now : new Date(now), timeZone);

    if (isWithinOfficeHours(nowParts, days, startHour, endHour)) return null;

    const currentMinutes = nowParts.hour * 60 + nowParts.minute;
    const startMinutes = startHour * 60;

    for (let offset = 0; offset <= 8; offset += 1) {
      const candidate = offset === 0 ? nowParts : partsForLocalOffset(nowParts, offset, timeZone);
      if (!days.includes(candidate.weekday)) continue;
      if (offset === 0 && currentMinutes >= startMinutes) continue;

      return {
        day: dayLabel(offset, candidate.weekday, locale, timeZone, config.dayLabels),
        locale,
        openingMinutes: startMinutes,
        responseMinutes,
      };
    }

    return null;
  }

  function fillTemplate(template, replacements) {
    return Object.entries(replacements).reduce(
      (message, [key, value]) => message.replaceAll(`[[${key}]]`, value),
      template,
    );
  }

  function emailReopenMessage(now, options) {
    const config = options || {};
    const opening = nextOpening(now, config);
    if (!opening) return '';

    const defaultTemplate = String(opening.locale || '').toLowerCase().startsWith('fr')
      ? 'À la reprise, [[day]] avant [[time]]'
      : 'When we reopen, [[day]] before [[time]]';

    return fillTemplate(config.messageTemplate || defaultTemplate, {
      day: opening.day,
      time: formatTime(opening.openingMinutes + opening.responseMinutes, opening.locale),
    });
  }

  function phoneReopenMessage(now, options) {
    const config = options || {};
    const opening = nextOpening(now, config);
    if (!opening) return '';

    const defaultTemplate = String(opening.locale || '').toLowerCase().startsWith('fr')
      ? 'Disponible [[day]] dès [[time]]'
      : 'Available [[day]] from [[time]]';
    let openingTime = formatTime(opening.openingMinutes, opening.locale);
    if (String(opening.locale || '').toLowerCase().startsWith('fr')) openingTime = openingTime.replace(/h00$/u, 'h');

    return fillTemplate(config.messageTemplate || defaultTemplate, {
      day: opening.day,
      time: openingTime,
    });
  }

  function lowerFirst(value) {
    const text = String(value || '');
    return text.charAt(0).toLowerCase() + text.slice(1);
  }

  function refreshEmailCard(card, now) {
    const textNode = card.querySelector('[data-contact-hub-email-text]');
    if (!textNode) return;

    if (!textNode.dataset.contactHubOriginalText) textNode.dataset.contactHubOriginalText = textNode.textContent;
    if (!card.dataset.contactHubOriginalAria) card.dataset.contactHubOriginalAria = card.getAttribute('aria-label') || '';

    const message = emailReopenMessage(now || new Date(), {
      days: parseDays(card.dataset.contactHubOfficeDays),
      startHour: card.dataset.contactHubOfficeStart,
      endHour: card.dataset.contactHubOfficeEnd,
      responseMinutes: card.dataset.contactHubResponseMinutes,
      locale: card.dataset.contactHubLocale || document.documentElement.lang || 'fr',
      timeZone: card.dataset.contactHubTimeZone || TIME_ZONE,
      messageTemplate: card.dataset.contactHubEmailTemplate,
      dayLabels: {
        today: card.dataset.contactHubTodayLabel,
        tomorrow: card.dataset.contactHubTomorrowLabel,
      },
    });

    if (message) {
      textNode.textContent = message;
      if (card.dataset.contactHubEmailAriaPrefix) {
        card.setAttribute('aria-label', `${card.dataset.contactHubEmailAriaPrefix}${lowerFirst(message)}`);
      }
      return;
    }

    textNode.textContent = card.dataset.contactHubActiveText || textNode.dataset.contactHubOriginalText;
    if (card.dataset.contactHubOriginalAria) card.setAttribute('aria-label', card.dataset.contactHubOriginalAria);
  }

  function refreshPhoneCard(card, now) {
    const textNode = card.querySelector('[data-contact-hub-phone-text]');
    if (!textNode) return;

    if (!textNode.dataset.contactHubOriginalText) textNode.dataset.contactHubOriginalText = textNode.textContent;
    if (!card.dataset.contactHubOriginalAria) card.dataset.contactHubOriginalAria = card.getAttribute('aria-label') || '';

    const message = phoneReopenMessage(now || new Date(), {
      days: parseDays(card.dataset.contactHubOfficeDays),
      startHour: card.dataset.contactHubOfficeStart,
      endHour: card.dataset.contactHubOfficeEnd,
      locale: card.dataset.contactHubLocale || document.documentElement.lang || 'fr',
      timeZone: card.dataset.contactHubTimeZone || TIME_ZONE,
      messageTemplate: card.dataset.contactHubPhoneTemplate,
      dayLabels: {
        today: card.dataset.contactHubTodayLabel,
        tomorrow: card.dataset.contactHubTomorrowLabel,
      },
    });

    if (message) {
      textNode.textContent = message;
      if (card.dataset.contactHubPhoneAriaPrefix) {
        card.setAttribute('aria-label', `${card.dataset.contactHubPhoneAriaPrefix}${lowerFirst(message)}`);
      }
      return;
    }

    textNode.textContent = card.dataset.contactHubActiveText || textNode.dataset.contactHubOriginalText;
    if (card.dataset.contactHubOriginalAria) card.setAttribute('aria-label', card.dataset.contactHubOriginalAria);
  }

  function refresh(root, now) {
    const scope = root || document;
    scope.querySelectorAll('[data-contact-hub-email-card]').forEach((card) => refreshEmailCard(card, now));
    scope.querySelectorAll('[data-contact-hub-phone-card]').forEach((card) => refreshPhoneCard(card, now));
  }

  function bind() {
    refresh(document);
    window.setInterval(() => refresh(document), 60000);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) refresh(document);
    });
    document.addEventListener('shopify:section:load', (event) => refresh(event.target));
  }

  window.TodayMerchContactHub = {
    emailReopenMessage,
    phoneReopenMessage,
    refresh,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
})();
